import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';

const ROOMS = [
  ...Array.from({ length: 15 }, (_, i) => `G${i + 1}`),
  ...Array.from({ length: 15 }, (_, i) => `F${i + 1}`),
  ...Array.from({ length: 15 }, (_, i) => `S${i + 1}`),
  ...Array.from({ length: 15 }, (_, i) => `T${i + 1}`)
];

const FLOOR_LABELS = { G: 'Ground Floor', F: 'First Floor', S: 'Second Floor', T: 'Third Floor' };

const emptyForm = {
  name: '', fatherName: '', cnicNo: '', address: '', institute: '',
  department: '', contactNo: '', emergencyParentContact: '', room: '',
  admissionFee: '', securityFee: '', monthlyFee: '',
  admissionFeePaid: 'Unpaid', securityFeePaid: 'Unpaid',
  admissionDate: new Date().toISOString().split('T')[0],
  status: 'Active'
};

export default function StudentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signature, setSignature] = useState(null);
  const [availableRooms, setAvailableRooms] = useState(ROOMS);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/rooms/available').then(r => {
      setAvailableRooms(isEdit ? ROOMS : r.data);
    });
    if (isEdit) {
      api.get(`/students/${id}`).then(r => {
        const s = r.data;
        setForm({
          name: s.name || '', fatherName: s.fatherName || '', cnicNo: s.cnicNo || '',
          address: s.address || '', institute: s.institute || '', department: s.department || '',
          contactNo: s.contactNo || '', emergencyParentContact: s.emergencyParentContact || '',
          room: s.room || '', admissionFee: s.admissionFee || '', securityFee: s.securityFee || '',
          monthlyFee: s.monthlyFee || '', admissionFeePaid: s.admissionFeePaid || 'Unpaid',
          securityFeePaid: s.securityFeePaid || 'Unpaid',
          admissionDate: s.admissionDate ? s.admissionDate.split('T')[0] : new Date().toISOString().split('T')[0],
          status: s.status || 'Active'
        });
        if (s.photo) setPhotoPreview(`/uploads/${s.photo}`);
      });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append('photo', photo);
      if (signature) fd.append('studentSignature', signature);

      if (isEdit) {
        await api.put(`/students/${id}`, fd);
        setSuccess('Student updated successfully!');
      } else {
        await api.post('/students', fd);
        setSuccess('Student added successfully!');
        setForm(emptyForm);
        setPhoto(null);
        setPhotoPreview(null);
      }
      setTimeout(() => navigate('/students'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save student');
    } finally {
      setLoading(false);
    }
  };

  const grouped = { G: [], F: [], S: [], T: [] };
  ROOMS.forEach(r => { grouped[r[0]].push(r); });

  return (
    <Layout title={isEdit ? 'Edit Student' : 'Add New Student'}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📋 Student Admission Form — Royal Tower Hostel</h3>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/students')}>← Back</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">✅ {success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Photo Upload */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20, padding: '16px 0', borderBottom: '1px solid #e2e8f0' }}>
            <div>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="photo-upload-preview" />
              ) : (
                <div className="photo-placeholder">📷</div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', display: 'block', marginBottom: 6 }}>
                STUDENT PHOTO
              </label>
              <input type="file" accept="image/*" onChange={handlePhotoChange} />
              <p style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>JPG, PNG, max 5MB</p>
            </div>
          </div>

          {/* Personal Info */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              👤 Personal Information
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Father Name *</label>
                <input value={form.fatherName} onChange={e => set('fatherName', e.target.value)} required placeholder="Father's full name" />
              </div>
              <div className="form-group">
                <label>CNIC No *</label>
                <input value={form.cnicNo} onChange={e => set('cnicNo', e.target.value)} required placeholder="XXXXX-XXXXXXX-X" />
              </div>
              <div className="form-group">
                <label>Contact No *</label>
                <input value={form.contactNo} onChange={e => set('contactNo', e.target.value)} required placeholder="03XX-XXXXXXX" />
              </div>
              <div className="form-group">
                <label>Emergency Parent Contact *</label>
                <input value={form.emergencyParentContact} onChange={e => set('emergencyParentContact', e.target.value)} required placeholder="Parent emergency number" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Address *</label>
                <textarea value={form.address} onChange={e => set('address', e.target.value)} required placeholder="Complete home address" rows={2} />
              </div>
            </div>
          </div>

          {/* Academic Info */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🎓 Academic Information
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Institute *</label>
                <input value={form.institute} onChange={e => set('institute', e.target.value)} required placeholder="University/College name" />
              </div>
              <div className="form-group">
                <label>Department *</label>
                <input value={form.department} onChange={e => set('department', e.target.value)} required placeholder="e.g. Computer Science" />
              </div>
            </div>
          </div>

          {/* Room Assignment */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🏠 Room Assignment
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Assign Room *</label>
                <select value={form.room} onChange={e => set('room', e.target.value)} required>
                  <option value="">-- Select Room --</option>
                  {Object.entries(grouped).map(([floor, rooms]) => (
                    <optgroup key={floor} label={FLOOR_LABELS[floor]}>
                      {rooms.map(r => (
                        <option key={r} value={r} disabled={!isEdit && !availableRooms.includes(r)}>
                          {r} {!isEdit && !availableRooms.includes(r) ? '(Occupied)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Admission Date</label>
                <input type="date" value={form.admissionDate} onChange={e => set('admissionDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              💰 Fee Structure
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Admission Fee (PKR)</label>
                <input type="number" value={form.admissionFee} onChange={e => set('admissionFee', e.target.value)} placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>Admission Fee Status</label>
                <select value={form.admissionFeePaid} onChange={e => set('admissionFeePaid', e.target.value)}>
                  <option>Unpaid</option>
                  <option>Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label>Security Fee (PKR)</label>
                <input type="number" value={form.securityFee} onChange={e => set('securityFee', e.target.value)} placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>Security Fee Status</label>
                <select value={form.securityFeePaid} onChange={e => set('securityFeePaid', e.target.value)}>
                  <option>Unpaid</option>
                  <option>Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label>Monthly Fee (PKR) *</label>
                <input type="number" value={form.monthlyFee} onChange={e => set('monthlyFee', e.target.value)} required placeholder="0" min="0" />
              </div>
            </div>
          </div>

          {/* Signature */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              ✍️ Signature
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Student Signature (optional)</label>
                <input type="file" accept="image/*" onChange={e => setSignature(e.target.files[0])} />
                <p style={{ fontSize: 11, color: '#718096' }}>Upload signature image</p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/students')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? '💾 Update Student' : '✅ Add Student'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
